import { useState } from 'react';
import {
  CommandCenterSidebar,
  SIDEBAR_WIDTH_CLASS,
} from '@/features/manager-command-center/components/CommandCenterSidebar';
import { PlatformGlyph } from '@/features/manager-breaks-demo/components/PlatformGlyph';
import {
  PLATFORM_ICON_BG,
  PLATFORM_TEXT,
} from '@/features/manager-breaks-demo/components/break-styles';
import { LEADERSHIP_IMPACT } from '../data/leadership-demo-dataset';
import type {
  ExecRoi,
  ResolvedPattern,
  RoiCostProject,
  RoiFormula,
  RoiProjectSignal,
} from '../types/leadership';

/** Contained library width - wide rows, never edge-to-edge on ultrawide. */
const LIBRARY_WIDTH = 'max-w-[1180px]';

/** The deep cost story reads as one centered narrative column. */
const NARRATIVE_WIDTH = 'mx-auto max-w-[940px]';

/* ──────────────────────────── Formatting ─────────────────────────── */

/** Compact USD for board-room reading: $1.2M, $864K, $770. */
function formatUsd(amount: number): string {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${Math.round(amount / 1_000)}K`;
  }
  return `$${amount}`;
}

/* ─────────────────────────── Shared bits ──────────────────────────── */

/** Numbered editorial section header: index · label, with a trailing hairline. */
function SectionEyebrow({ index, label }: { index: string; label: string }) {
  return (
    <div className="mb-8 flex items-center gap-4">
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
        <span className="text-[#0089bd]">{index}</span>
        <span className="mx-2 text-zinc-300">·</span>
        {label}
      </span>
      <span className="h-px flex-1 bg-zinc-200/80" />
    </div>
  );
}

/** Pattern chip - the recurring mistake this cost belongs to. */
function PatternBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex w-max items-center gap-1.5 rounded-full border border-[#00AEEF]/15 bg-[#00AEEF]/[0.06] px-2.5 py-1 text-xs font-semibold text-[#0089bd]">
      <span className="h-1.5 w-1.5 rounded-full bg-[#00AEEF]" />
      {label}
    </span>
  );
}

/** The proportional rework-vs-incident split for one project. */
function CostSplitBar({ project }: { project: RoiCostProject }) {
  const reworkPercent = (project.reworkUsd / project.avertedUsd) * 100;
  return (
    <span
      className="flex h-1.5 w-full overflow-hidden rounded-full bg-zinc-100"
      aria-hidden="true"
    >
      <span
        className="h-full bg-[#00AEEF]"
        style={{ width: `${reworkPercent}%` }}
      />
      <span className="h-full flex-1 bg-rose-400" />
    </span>
  );
}

/* ───────────────────── ROI summary (the headline) ─────────────────── */

/** One formula tile - a line of the calculation behind the headline. */
function FormulaStat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone: string;
}) {
  return (
    <div className="rounded-3xl border border-zinc-100 bg-white p-7 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)]">
      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
        {label}
      </p>
      <p className={`mt-3 text-4xl font-semibold tracking-tight ${tone}`}>
        {value}
      </p>
      <p className="mt-2 text-sm text-zinc-500">{sub}</p>
    </div>
  );
}

/**
 * The headline band - one dominant averted-cost figure with the two cost lines
 * that build it, so a CFO sees how the number is calculated before drilling in.
 */
function RoiSummary({ roi }: { roi: ExecRoi }) {
  const { formula } = roi;
  return (
    <div className="mt-10 overflow-hidden rounded-[2rem] border border-zinc-100 bg-white p-8 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)] md:p-10">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-center lg:gap-12">
        {/* The figure */}
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0089bd]">
            {roi.eyebrow}
          </span>
          <p className="mt-4 flex items-start text-[#0E1B3A]">
            <span className="mt-2 text-3xl font-light text-zinc-300 md:mt-4 md:text-4xl">
              $
            </span>
            <span className="text-[4.5rem] font-extralight leading-[0.9] tracking-tighter md:text-[6.5rem]">
              {roi.avertedValue}
            </span>
            <span className="mt-2 text-3xl font-light text-zinc-400 md:mt-5 md:text-5xl">
              {roi.avertedUnit}
            </span>
          </p>
          <p className="mt-5 max-w-md text-base font-light leading-relaxed text-zinc-500">
            {roi.caption}
          </p>
        </div>

        {/* How it is built */}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormulaStat
            label="Rework & slip"
            value={formatUsd(formula.reworkUsd)}
            sub={`${formula.totalEngDays} eng-days × ${formatUsd(
              formula.ratePerEngDayUsd
            )}/day`}
            tone="text-[#0089bd]"
          />
          <FormulaStat
            label="Incident response"
            value={formatUsd(formula.incidentUsd)}
            sub={`${formula.totalP1} P1 × ${formatUsd(formula.p1CostUsd)} each`}
            tone="text-rose-600"
          />
        </div>
      </div>

      <p className="mt-8 border-t border-zinc-100 pt-6 text-[11px] leading-relaxed text-zinc-400">
        {roi.footnote}
      </p>
    </div>
  );
}

/* ─────────────────────── Library (cost dossiers) ──────────────────── */

/** One footer stat on a dossier card. */
function CardStat({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: string;
  unit?: string;
  tone: string;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
        {label}
      </p>
      <p className="flex items-baseline gap-1">
        <span className={`text-xl font-semibold tracking-tight ${tone}`}>
          {value}
        </span>
        {unit && (
          <span className="text-xs font-medium text-zinc-400">{unit}</span>
        )}
      </p>
    </div>
  );
}

/** One half-width cost dossier - the whole surface opens the deep breakdown. */
function CostDossierCard({
  project,
  onOpen,
}: {
  project: RoiCostProject;
  onOpen: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(project.id)}
      className="group relative flex w-full flex-col overflow-hidden rounded-[2rem] border border-zinc-100 bg-white p-7 text-start shadow-[0_24px_60px_-50px_rgba(16,27,58,0.5)] transition-all hover:-translate-y-0.5 hover:shadow-[0_30px_70px_-44px_rgba(16,27,58,0.5)] md:p-8"
    >
      {/* Hover accent rail */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-7 start-0 w-1 origin-top scale-y-0 rounded-full bg-[#00AEEF] transition-transform duration-300 group-hover:scale-y-100"
      />

      {/* Identity */}
      <div className="flex items-center justify-between gap-3">
        <PatternBadge label={project.patternLabel} />
        <span className="text-2xl font-light tracking-tight text-[#0E1B3A]">
          {formatUsd(project.avertedUsd)}
        </span>
      </div>
      <h3 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 transition-colors group-hover:text-[#0089bd] md:text-2xl">
        {project.name}
      </h3>
      <p className="mt-1 text-sm text-zinc-500">{project.team}</p>
      <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-zinc-500">
        {project.whyItCostSoMuch}
      </p>

      {/* Cost split + stat row */}
      <div className="mt-6 border-t border-zinc-100 pt-5">
        <CostSplitBar project={project} />
        <div className="mt-5 flex w-full items-end justify-between gap-4">
          <div className="grid flex-1 grid-cols-2 gap-4">
            <CardStat
              label="Rework & slip"
              value={String(project.engDays)}
              unit="eng-days"
              tone="text-[#0089bd]"
            />
            <CardStat
              label="Incidents"
              value={String(project.p1Count)}
              unit="P1"
              tone="text-rose-600"
            />
          </div>
          <span className="pb-1 text-zinc-300 transition-colors group-hover:text-[#0089bd]">
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6 rtl:rotate-180"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </div>
      </div>
    </button>
  );
}

function RoiLibrary({
  roi,
  periodLabel,
  onOpen,
}: {
  roi: ExecRoi;
  periodLabel: string;
  onOpen: (id: string) => void;
}) {
  return (
    <div className={`mx-auto w-full ${LIBRARY_WIDTH}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">
        Executive impact · {periodLabel}
      </p>
      <h1 className="mt-2 text-4xl font-semibold tracking-tight text-zinc-900 md:text-5xl">
        Return on Intera
      </h1>
      <p className="mt-3 max-w-2xl text-lg leading-relaxed text-zinc-500">
        The same execution mistakes the team catches one at a time, totalled
        across the portfolio - what they would have cost, and which project each
        dollar came from.
      </p>

      <RoiSummary roi={roi} />

      <div className="mt-12 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold tracking-tight text-zinc-900">
          Where it came from
        </h2>
        <span className="text-xs font-medium text-zinc-400">
          {roi.projects.length} projects · costliest first
        </span>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        {roi.projects.map((project) => (
          <CostDossierCard key={project.id} project={project} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── 01 · The cost (derived figure) ───────────────── */

/** One headline metric tile in the detail view. */
function CaseStat({
  label,
  value,
  unit,
  sub,
  tone,
}: {
  label: string;
  value: string;
  unit?: string;
  sub: string;
  tone: string;
}) {
  return (
    <div className="rounded-3xl border border-zinc-100 bg-white p-7 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)]">
      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
        {label}
      </p>
      <p className="mt-3 flex items-baseline gap-1.5">
        <span className={`text-4xl font-semibold tracking-tight ${tone}`}>
          {value}
        </span>
        {unit && (
          <span className="text-sm font-medium text-zinc-400">{unit}</span>
        )}
      </p>
      <p className="mt-2 text-sm text-zinc-500">{sub}</p>
    </div>
  );
}

function CostSection({
  project,
  formula,
}: {
  project: RoiCostProject;
  formula: RoiFormula;
}) {
  return (
    <section>
      <SectionEyebrow index="01" label="The cost" />

      <div className="grid gap-4 md:grid-cols-3">
        <CaseStat
          label="Total averted"
          value={formatUsd(project.avertedUsd)}
          sub="On this project alone"
          tone="text-[#0E1B3A]"
        />
        <CaseStat
          label="Rework & slip"
          value={formatUsd(project.reworkUsd)}
          sub={`${project.engDays} eng-days × ${formatUsd(
            formula.ratePerEngDayUsd
          )}/day`}
          tone="text-[#0089bd]"
        />
        <CaseStat
          label="Incident response"
          value={formatUsd(project.incidentUsd)}
          sub={`${project.p1Count} P1 × ${formatUsd(formula.p1CostUsd)} each`}
          tone="text-rose-600"
        />
      </div>

      {/* The split, made visual. */}
      <div className="mt-5 rounded-[2rem] border border-zinc-100 bg-white p-8 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)] md:p-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400">
            How the cost splits
          </p>
          <p className="text-xs font-medium text-zinc-400">
            Averted, before it could be spent
          </p>
        </div>
        <div className="mt-6">
          <CostSplitBar project={project} />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs font-medium text-zinc-500">
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#00AEEF]" />
            Rework & slipped delivery - {formatUsd(project.reworkUsd)}
          </span>
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
            Incident response - {formatUsd(project.incidentUsd)}
          </span>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── 02 · What Intera correlated (evidence) ───────────── */

/** Uppercase source label from a platform key, e.g. "pagerduty" → "PagerDuty". */
const SOURCE_LABEL: Partial<Record<RoiProjectSignal['source'], string>> = {
  jira: 'Jira',
  github: 'GitHub',
  pagerduty: 'PagerDuty',
  confluence: 'Confluence',
  slack: 'Slack',
};

/** One correlated artifact - the evidence trail behind the cost. */
function SignalRow({ signal }: { signal: RoiProjectSignal }) {
  return (
    <div className="flex items-start gap-4 py-5">
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          PLATFORM_ICON_BG[signal.source]
        }`}
      >
        <PlatformGlyph
          platform={signal.source}
          className={`h-4 w-4 ${PLATFORM_TEXT[signal.source]}`}
        />
      </span>
      <div className="min-w-0">
        <p className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-zinc-900">
            {signal.ref}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
            {SOURCE_LABEL[signal.source] ?? signal.source}
          </span>
        </p>
        <p className="mt-1 text-[13px] leading-relaxed text-zinc-500">
          {signal.description}
        </p>
      </div>
    </div>
  );
}

function EvidenceSection({ project }: { project: RoiCostProject }) {
  return (
    <section>
      <SectionEyebrow index="02" label="What Intera correlated" />

      <p className="max-w-2xl text-lg leading-relaxed text-zinc-600">
        No single tool held the cost. Intera stitched these fragments into one
        traceable number.
      </p>

      <div className="mt-6 divide-y divide-zinc-100 rounded-[2rem] border border-zinc-100 bg-white px-7 shadow-[0_24px_60px_-54px_rgba(16,27,58,0.5)] md:px-9">
        {project.signals.map((signal) => (
          <SignalRow key={signal.ref} signal={signal} />
        ))}
      </div>
    </section>
  );
}

/* ───────────────── 03 · Now a guardrail (the fix) ─────────────────── */

function GuardrailSection({ pattern }: { pattern: ResolvedPattern }) {
  return (
    <section>
      <SectionEyebrow index="03" label="Now a guardrail" />

      <div className="rounded-[2rem] border border-zinc-100 bg-white p-8 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)] md:p-10">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100">
            <svg
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5 text-emerald-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">
            Now enforced
          </span>
        </div>
        <h3 className="mt-3 text-2xl font-semibold tracking-tight text-zinc-900">
          {pattern.enforcedName}
        </h3>
        <p className="mt-3 text-[15px] leading-relaxed text-zinc-600 md:text-base">
          {pattern.enforcedDetail}
        </p>
        <p className="mt-6 flex items-center gap-2 border-t border-zinc-100 pt-5 text-sm font-medium text-zinc-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Caught {pattern.timesCaught}× across {pattern.projectsSpanned}{' '}
          projects · {pattern.guardingLabel}
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────── Deep cost breakdown ──────────────────────── */

function CostDetail({
  project,
  pattern,
  formula,
  onBack,
}: {
  project: RoiCostProject;
  pattern: ResolvedPattern | undefined;
  formula: RoiFormula;
  onBack: () => void;
}) {
  return (
    <div className={NARRATIVE_WIDTH}>
      <button
        type="button"
        onClick={onBack}
        className="group flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-800"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4 transition-transform group-hover:-translate-x-0.5 rtl:rotate-180 rtl:group-hover:translate-x-0.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M19 12H5M11 18l-6-6 6-6" />
        </svg>
        Back to the breakdown
      </button>

      {/* Hero */}
      <header className="mt-10">
        <div className="flex flex-wrap items-center gap-3">
          <PatternBadge label={project.patternLabel} />
          <span className="text-[11px] font-medium text-zinc-400">
            {project.team}
          </span>
        </div>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 md:text-5xl">
            {project.name}
          </h1>
          <p className="flex items-baseline gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
              Averted
            </span>
            <span className="text-3xl font-light tracking-tight text-[#0E1B3A] md:text-4xl">
              {formatUsd(project.avertedUsd)}
            </span>
          </p>
        </div>
        <p className="mt-6 border-s-2 border-[#00AEEF] ps-5 text-lg font-light leading-relaxed text-zinc-600">
          {project.whyItCostSoMuch}
        </p>
      </header>

      {/* The narrative spine */}
      <div className="mt-16 space-y-20">
        <CostSection project={project} formula={formula} />
        <EvidenceSection project={project} />
        {pattern && <GuardrailSection pattern={pattern} />}
      </div>
    </div>
  );
}

/* ───────────────────────────── Page ───────────────────────────────── */

/**
 * The leadership surface, told like a retrospective but for money: a library of
 * cost dossiers - one per project behind the averted-cost figure - sitting under
 * a summary band that shows how the headline is calculated. Opening a dossier
 * tells that project's cost as a single scroll: what it cost (derived, not
 * asserted), the artifacts Intera correlated to trace it, and the guardrail that
 * now closes the mistake for good.
 */
export function LeadershipPage() {
  const impact = LEADERSHIP_IMPACT;
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);

  const openProject = openProjectId
    ? impact.roi.projects.find((project) => project.id === openProjectId)
    : null;
  const openPattern = openProject
    ? impact.patterns.find((pattern) => pattern.id === openProject.patternId)
    : undefined;

  return (
    <div className="relative min-h-[100dvh] bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_60%,#f3f6fb_100%)] text-black">
      <div className="pointer-events-none absolute inset-x-0 -top-24 h-72 bg-[radial-gradient(58%_100%_at_50%_0%,rgba(0,174,239,0.07),transparent_72%)]" />

      <CommandCenterSidebar activeTab="leadership" />

      <div className={SIDEBAR_WIDTH_CLASS}>
        <main className="relative z-10 w-full px-5 pb-24 pt-8 md:px-10">
          {openProject ? (
            <CostDetail
              project={openProject}
              pattern={openPattern}
              formula={impact.roi.formula}
              onBack={() => setOpenProjectId(null)}
            />
          ) : (
            <RoiLibrary
              roi={impact.roi}
              periodLabel={impact.periodLabel}
              onOpen={setOpenProjectId}
            />
          )}
        </main>
      </div>
    </div>
  );
}
